import { Permissions } from "@nova/constants"
import {
    ActivityDisplay,
    EntityType,
    EventKind,
    eventPayloads,
    LayerEntityType,
} from "@nova/schemas"
import { entityTypeLabels, layerTypeLabels, subjectLabel } from "../events/eventLabels"

export type ActivityEvent = {
    id: string
    kind: string
    subject_type: string | null
    project_id: string | null
    created_by: string
    created_at: Date | null
    data: unknown
    display?: ActivityDisplay
}

export type ActivityCopyContext = {
    actorName: string | undefined
    nameFor?: (userId: string) => string | undefined
    count?: number
}

const startsWithVowel = (word: string) => /^[aeiou]/i.test(word)

const quantify = (noun: string, count: number, plural = `${noun}s`) =>
    count > 1 ? `${count} ${plural}` : `${startsWithVowel(noun) ? "an" : "a"} ${noun}`

const subject = (event: ActivityEvent, fallback: string) =>
    subjectLabel(event.subject_type) ?? fallback

const layerNoun = (event: ActivityEvent) => {
    const type = event.subject_type
    if (type && Object.hasOwn(layerTypeLabels, type))
        return `${layerTypeLabels[type as LayerEntityType]} layer`
    if (type && Object.hasOwn(entityTypeLabels, type)) return entityTypeLabels[type as EntityType]
    return "layer"
}

const layerCopy = (verb: string) => (event: ActivityEvent, ctx: ActivityCopyContext) =>
    `${verb} ${quantify(layerNoun(event), ctx.count ?? 1)}`

const systemCopy = (event: ActivityEvent, ctx: ActivityCopyContext, outcome: string) => {
    const noun = quantify(layerNoun(event), ctx.count ?? 1)
    const subject = noun.charAt(0).toUpperCase() + noun.slice(1)
    return `${subject} ${outcome}${ctx.actorName ? ` by ${ctx.actorName}` : ""}`
}

const roleNames: Record<number, string> = {
    [Permissions.Admin]: "Admin",
    [Permissions.Editor]: "Editor",
    [Permissions.Viewer]: "Viewer",
}

const accessTarget = (event: ActivityEvent, ctx: ActivityCopyContext) => {
    const granted = eventPayloads[EventKind.AccessGranted].safeParse(event.data)
    const removed = eventPayloads[EventKind.AccessRemoved].safeParse(event.data)
    const targetId = granted.success ? granted.data.targetUserId : removed.data?.targetUserId
    const name = targetId ? ctx.nameFor?.(targetId) : undefined
    return {
        name: name ?? "someone",
        role: granted.success ? roleNames[granted.data.permission] : undefined,
    }
}

type CopyFn = (event: ActivityEvent, ctx: ActivityCopyContext) => string

const copyByKind: Record<EventKind, CopyFn> = {
    [EventKind.LayerCreated]: layerCopy("Created"),
    [EventKind.LayerDeleted]: layerCopy("Deleted"),
    [EventKind.LayerProcessed]: (e, ctx) => systemCopy(e, ctx, "finished processing"),
    [EventKind.LayerProcessingFailed]: (e, ctx) => systemCopy(e, ctx, "failed to process"),
    [EventKind.LayerArchived]: layerCopy("Archived"),
    [EventKind.LayerUnarchived]: layerCopy("Restored"),
    [EventKind.EntityCreated]: layerCopy("Created"),
    [EventKind.EntityDeleted]: layerCopy("Deleted"),
    [EventKind.EntityArchived]: layerCopy("Archived"),
    [EventKind.EntityUnarchived]: layerCopy("Restored"),
    [EventKind.StreamStarted]: (_, ctx) => `Started ${quantify("Live Stream", ctx.count ?? 1)}`,
    [EventKind.StreamEnded]: (_, ctx) => `Ended ${quantify("Live Stream", ctx.count ?? 1)}`,
    [EventKind.LocationSharingStarted]: () => "Started sharing their location",
    [EventKind.LocationSharingEnded]: () => "Stopped sharing their location",
    [EventKind.StreamShared]: (_, ctx) => `Shared ${quantify("Live Stream", ctx.count ?? 1)}`,
    [EventKind.MissionGroupStreamShared]: () => "Shared a mission group live stream",
    [EventKind.MissionGroupDashboardShared]: () => "Shared a mission group dashboard",
    [EventKind.PdfGenerated]: (e, ctx) =>
        `Generated ${quantify(subject(e, "PDF"), ctx.count ?? 1)}`,
    [EventKind.PdfFailed]: () => "A PDF export failed",
    [EventKind.CommentCreated]: (e) =>
        e.display?.kind === "comment" ? e.display.text : "Commented",
    [EventKind.CommentDeleted]: (e, ctx) => {
        const count = ctx.count ?? 1
        const isReply = e.display?.kind === "comment" && !!e.display.parentAuthorId
        return `Deleted ${quantify(isReply ? "reply" : "comment", count, isReply ? "replies" : "comments")}`
    },
    [EventKind.AccessRequested]: () => "Requested access",
    [EventKind.AccessGranted]: (e, ctx) => {
        const { name, role } = accessTarget(e, ctx)
        if (!role) return `Granted ${name} access`
        return `Made ${name} ${startsWithVowel(role) ? "an" : "a"} ${role}`
    },
    [EventKind.AccessRemoved]: (e, ctx) => `Revoked ${accessTarget(e, ctx).name}'s access`,
}

const isKnownKind = (kind: string): kind is EventKind => Object.hasOwn(copyByKind, kind)

export const activityCopy = (event: ActivityEvent, ctx: ActivityCopyContext) =>
    isKnownKind(event.kind) ? copyByKind[event.kind](event, ctx) : event.kind

export const relativeTime = (date: Date | null, now = new Date()) => {
    if (!date) return ""

    const seconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000))
    if (seconds < 60) return "now"

    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} min ago`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`

    return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(date)
}

export const dayHeading = (date: Date, now = new Date()) => {
    const startOfDay = (value: Date) =>
        new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()

    const dayMs = 24 * 60 * 60 * 1000
    const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / dayMs)

    if (diffDays === 0) return "TODAY"
    if (diffDays === 1) return "YESTERDAY"
    if (diffDays <= 7) return "LAST 7 DAYS"
    return "OLDER"
}

export const sectionTimestamp = (date: Date | null, now = new Date()) => {
    if (!date) return ""

    const heading = dayHeading(date, now)
    if (heading === "TODAY") return relativeTime(date, now)

    const time = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(
        date
    )
    if (heading === "YESTERDAY") return time

    const day = new Intl.DateTimeFormat(
        "en-US",
        heading === "LAST 7 DAYS" ? { weekday: "long" } : { month: "short", day: "numeric" }
    ).format(date)

    return `${day} ${time}`
}
